/* === about-page.jsx === */
// Solara Living - About page

const { useState, useEffect, useRef } = React;

// --- CMS content lookup helpers. about-page is the one page that doesn't load
// solara-form.jsx, so we define them here too. Pattern matches solara-form.jsx.
function _C(path, fallback){
  const root = (typeof window !== "undefined" && window.__SOLARA_CONTENT__) || (typeof globalThis !== "undefined" && globalThis.__SOLARA_CONTENT__) || null;
  if (!root) return fallback;
  const v = String(path).split('.').reduce((o, k) => (o == null ? o : o[k]), root);
  return (v === undefined || v === null || v === '') ? fallback : v;
}
function _CA(path, fallback){
  const v = _C(path, null);
  return Array.isArray(v) && v.length ? v : fallback;
}

function Nav({ onDark }) {
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setMenuOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [menuOpen]);
  const close = () => setMenuOpen(false);
  return (
    <>
      <div className={`nav ${onDark ? "on-dark" : ""}`}>
        <div className="nav-pill">
          <a href="Solara Living.html" className="nav-brand">
            <img className="mark" src="images/solara-logo.svg" alt="" aria-hidden="true" />
            <span>SOLARA <b>LIVING</b></span>
          </a>
          <a href="Solara Living.html" className="nav-link">Home</a>
          <a href="about.html" className="nav-link active">About</a>
          <div className="nav-dropdown nav-link" style={{padding:0}}>
            <a href="#" className="nav-link" style={{display:"inline-flex", alignItems:"center", gap:6}}>
              Products
              <svg width="9" height="9" viewBox="0 0 10 10" fill="none"><path d="M2 3.5L5 6.5L8 3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </a>
            <div className="nav-dropdown-menu">
              <a href="pergolas.html">Pergolas</a>
              <a href="saunas.html">Saunas</a>
              <a href="hot-tubs.html">Hot tubs</a>
              <a href="swim-spas.html">Swim spas</a>
            </div>
          </div>
          <a href="contact.html" className="nav-link">Contact</a>
          <button type="button" className="nav-hamburger" aria-label={menuOpen ? "Close menu" : "Open menu"} aria-expanded={menuOpen} onClick={() => setMenuOpen(o => !o)}>
            {menuOpen ? (
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M4 7h16M4 12h16M4 17h16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
            )}
          </button>
          <a href="contact.html" className="nav-cta">Request a quote</a>
        </div>
      </div>
      <div className={`nav-drawer-backdrop ${menuOpen ? "open" : ""}`} onClick={close} aria-hidden="true" />
      <div className={`nav-drawer ${menuOpen ? "open" : ""}`} role="dialog" aria-modal="true" aria-label="Site menu">
        <div className="nav-drawer-section">Navigate</div>
        <a href="Solara Living.html" className="nav-drawer-link" onClick={close}>Home <span className="arrow">→</span></a>
        <a href="about.html" className="nav-drawer-link active" onClick={close}>About <span className="arrow">→</span></a>
        <a href="contact.html" className="nav-drawer-link" onClick={close}>Contact <span className="arrow">→</span></a>
        <div className="nav-drawer-section">Products</div>
        <a href="pergolas.html" className="nav-drawer-link" onClick={close}>Pergolas <span className="arrow">→</span></a>
        <a href="saunas.html" className="nav-drawer-link" onClick={close}>Saunas <span className="arrow">→</span></a>
        <a href="hot-tubs.html" className="nav-drawer-link" onClick={close}>Hot tubs <span className="arrow">→</span></a>
        <a href="swim-spas.html" className="nav-drawer-link" onClick={close}>Swim spas <span className="arrow">→</span></a>
      </div>
    </>
  );
}

function useReveal(){
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) e.target.classList.add("in"); });
    }, { threshold: 0.18 });
    el.querySelectorAll('[data-reveal]').forEach(n => io.observe(n));
    return () => io.disconnect();
  }, []);
  return ref;
}

// ---- Hero ----
function Hero(){
  const headingRaw = _C('about.hero.heading', 'Built for life,\nin Canada.');
  const headingHtml = String(headingRaw).replace(/\n/g, '<br/>');
  const sub = _C('about.hero.subheading', 'Solara Living is a Vaughan-based outdoor living studio. We design, deliver, and install pergolas, saunas, and hot tubs across the GTA and southern Ontario. Refined, reliable, and built to last.');
  const heroSrc = _C('about.hero.src', 'images/gallery/pergola-twilight.jpg');
  const heroAlt = _C('about.hero.alt', 'A Solara Living install at dusk: white aluminum pergola, lounge seating, garden beds.');
  const primaryBtn = _C('about.hero.primaryButtonText', 'Request a quote');
  const primaryLink = _C('about.hero.primaryButtonLink', 'contact.html');
  const secondaryBtn = _C('about.hero.secondaryButtonText', 'Read our story');
  const breadcrumb = _C('about.breadcrumbLeaf', 'About');
  return (
    <section className="p-hero">
      <div className="p-hero-photo">
        <img src={heroSrc} alt={heroAlt} />
      </div>
      <div className="p-hero-veil" />
      <div className="p-breadcrumb wrap">
        <a href="Solara Living.html"><span className="arrow">←</span> Solara Living</a> &nbsp;·&nbsp; {breadcrumb}
      </div>
      <div className="wrap p-hero-inner">
        <div className="p-hero-copy">
          <h1 className="p-hero-headline" dangerouslySetInnerHTML={{__html: headingHtml}} />
          <div className="p-hero-sub-block">
            <p className="p-hero-sub">{sub}</p>
            <div className="p-hero-actions">
              <a className="btn" href={primaryLink}>{primaryBtn} <span className="arrow">↗</span></a>
              <a className="btn ghost" href="#story">{secondaryBtn} <span className="arrow">→</span></a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ---- Marquee ----
function MarqueeStrip(){
  const items = _CA('about.marqueeItems', [
    "Premium · Canadian-made",
    "Lifetime warranty on pergolas + saunas",
    "Authorized SunLouvre + Northern Leisure dealer",
    "Residential + commercial",
    "GTA + southern Ontario",
    "Vaughan studio · by appointment",
  ]);
  const all = [...items, ...items, ...items];
  return (
    <div className="note-strip">
      <div className="marquee">
        {all.map((t,i)=>(<span key={i}><b>·</b>&nbsp; {t}</span>))}
      </div>
    </div>
  );
}

// ---- Story ----
function Story(){
  const ref = useReveal();
  const eyebrow = _C('about.storyEyebrow', 'About Solara Living');
  // Heading uses *markdown-style italic* syntax for the inline em tag.
  const headingRaw = _C('about.storyHeading', 'Premium outdoor living, *made in Canada*, made to last.');
  const headingHtml = String(headingRaw).replace(/\*([^*]+)\*/g, '<span class="ital">$1</span>');
  const paragraphs = _CA('about.storyBody', [
    'At Solara Living, we are committed to offering premium, Canadian-made products built with quality, craftsmanship, and lasting performance in mind.',
    'We specialize in custom aluminum pergolas from SunLouvre Pergolas, along with high-quality saunas and hot tubs from Northern Leisure Products. Our pergolas and saunas are backed by a limited lifetime warranty, reflecting the confidence behind their design and durability. Our hot tubs are designed for life in Canada and built to the highest level of quality.',
    'With a personalized, hands-on approach, we work closely with you to bring your vision to life with solutions that are refined, reliable, and built to last.',
  ]);
  const storySrc = _C('about.story.src', 'images/gallery/pergola-poolside-deck.jpg');
  const storyAlt = _C('about.story.alt', 'A Solara Living installed pergola over a poolside deck');
  return (
    <section className="a-story" id="story" ref={ref}>
      <div className="wrap a-story-grid">
        <div className="a-story-photo" data-reveal>
          <div className="ph has-photo">
            <img className="ph-img" src={storySrc} alt={storyAlt} />
          </div>
        </div>
        <div className="a-story-text">
          <span className="eyebrow" data-reveal>{eyebrow}</span>
          <h2 className="h2 a-story-h" data-reveal dangerouslySetInnerHTML={{__html: headingHtml}} />
          {paragraphs.map((p, i) => (
            <p key={i} className="body-lg a-story-p" data-reveal>{p}</p>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---- Brand partners (SunLouvre, Northern Leisure) ----
function Partners(){
  const ref = useReveal();
  const fallback = [
    {mark: "SLP", name: "SunLouvre Pergolas", where: "Built in Quebec",
     body: "Bioclimatic aluminum pergolas with adjustable louvres. Open for sun, tilt for breeze, close for rain. 100% waterproof when shut. Lifetime limited warranty on aluminum and the louvre mechanism.",
     src: "images/gallery/pergola-modern-house.jpg", href: "pergolas.html"},
    {mark: "NLP", name: "Northern Leisure Products", where: "Designed in Ontario",
     body: "Canadian Red Cedar saunas, plug-and-play and premium hot tubs, and North Stream all-season swim spas. Lifetime structural guarantee on premium tubs. Saunas covered by a lifetime limited warranty.",
     src: "images/gallery/sauna-cabin-forest.jpg", href: "saunas.html"},
  ];
  const items = _CA('about.partners', fallback);
  const eyebrow = _C('about.partnersEyebrow', 'Our partners');
  const headingRaw = _C('about.partnersHeading', 'Two best-in-class makers.\nOne curated catalogue.');
  const headingHtml = String(headingRaw).replace(/\n/g, '<br/>');
  const body = _C('about.partnersBody', "We don't sell what we wouldn't put in our own backyard. Every product we carry comes from a Canadian manufacturer we've vetted, visited, and stand behind for the long haul.");
  return (
    <section className="a-partners" ref={ref}>
      <div className="wrap">
        <div className="p-section-head" data-reveal>
          <span className="eyebrow">{eyebrow}</span>
          <h2 className="h2" dangerouslySetInnerHTML={{__html: headingHtml}} />
          <p className="body-lg">{body}</p>
        </div>
        <div className="a-partners-grid">
          {items.map((p,i) => (
            <a key={p._key || i} href={p.href} className="a-partner" data-reveal>
              <div className="a-partner-photo">
                <div className="ph v-pergola has-photo">
                  <img className="ph-img" src={p.src || p.photo} alt={p.alt || p.name} />
                </div>
                <div className="a-partner-mark">{p.mark}</div>
              </div>
              <div className="a-partner-body">
                <div className="a-partner-meta">
                  <span className="eyebrow">{p.where}</span>
                </div>
                <h3 className="a-partner-name">{p.name}</h3>
                <p className="body">{p.body || p.d}</p>
                <span className="a-partner-cta">Explore the lineup <span className="arrow">→</span></span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---- Approach / values ----
function Approach(){
  const ref = useReveal();
  const fallback = [
    { num:"01", heading:"Premium, Canadian-made",   body:"Manufactured in Quebec and Ontario by partners we've vetted. Built for Canadian winters and Canadian backyards." },
    { num:"02", heading:"Lifetime warranty",         body:"Aluminum pergolas and Northern Leisure saunas carry a limited lifetime warranty. Premium hot tubs carry a lifetime structural guarantee." },
    { num:"03", heading:"Custom or standard",        body:"Pergolas are available fully custom, or from a strong selection of standard sizes that suit most spaces." },
    { num:"04", heading:"Hands-on, end-to-end",      body:"Site visit, design, permits, install, and after-care. One point of contact from quote to closeout." },
  ];
  const items = _CA('about.approach', fallback);
  const eyebrow = _C('about.approachEyebrow', 'How we work');
  const headingRaw = _C('about.approachHeading', 'Refined. Reliable.\nBuilt to last.');
  const headingHtml = String(headingRaw).replace(/\n/g, '<br/>');
  return (
    <section className="a-approach" id="approach" ref={ref}>
      <div className="wrap">
        <div className="p-section-head" data-reveal>
          <span className="eyebrow">{eyebrow}</span>
          <h2 className="h2" dangerouslySetInnerHTML={{__html: headingHtml}} />
        </div>
        <div className="a-approach-grid">
          {items.map((it,i) => (
            <div className="a-approach-item" key={it._key || i} data-reveal>
              <div className="num">{it.num}</div>
              <h3>{it.heading || it.h}</h3>
              <p>{it.body || it.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---- Scope band ----
function Scope(){
  const ref = useReveal();
  const fallback = [
    { heading:"Residential",     body:"From single backyards to estate-scale builds.",                 src:"images/gallery/backyard-pergola-tub.jpg" },
    { heading:"Restaurants",     body:"Year-round patios. Heated, louvred, branded.",                  src:"images/gallery/pergola-farmhouse.jpg" },
    { heading:"Golf & resorts",  body:"Wellness pavilions, bar shelters, and event terraces.",         src:"images/gallery/pergola-glass-deck.jpg" },
    { heading:"Developments",    body:"Rooftop decks and amenity spaces, spec'd to bylaw.",            src:"images/gallery/pergola-stone-house.jpg" },
  ];
  const items = _CA('about.scope', fallback);
  const eyebrow = _C('about.scopeEyebrow', 'Where we work');
  const headingRaw = _C('about.scopeHeading', 'Backyards. Patios.\nWhole developments.');
  const headingHtml = String(headingRaw).replace(/\n/g, '<br/>');
  const body = _C('about.scopeBody', 'We work on a wide range of projects, from residential backyards to commercial spaces including restaurants, golf courses, and large-scale developments.');
  return (
    <section className="a-scope" ref={ref}>
      <div className="wrap">
        <div className="p-section-head" data-reveal>
          <span className="eyebrow">{eyebrow}</span>
          <h2 className="h2" dangerouslySetInnerHTML={{__html: headingHtml}} />
          <p className="body-lg">{body}</p>
        </div>
        <div className="a-scope-grid">
          {items.map((it,i) => (
            <div className="a-scope-tile" key={it._key || i} data-reveal>
              <div className="a-scope-photo">
                <div className="ph has-photo"><img className="ph-img" src={it.src} alt={it.alt || it.heading || it.k} /></div>
              </div>
              <div className="a-scope-meta">
                <h3>{it.heading || it.k}</h3>
                <p>{it.body || it.d}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---- Quote ----
function Quote(){
  const ref = useReveal();
  const text = _C('about.quoteText', 'With a personalized, hands-on approach, we work closely with you to bring your vision to life, with solutions that are refined, reliable, and built to last.');
  const attrib = _C('about.quoteAttribution', 'The Solara Living team · Vaughan, Ontario');
  return (
    <section className="a-quote" ref={ref}>
      <div className="wrap">
        <div className="a-quote-inner" data-reveal>
          <div className="a-quote-mark">"</div>
          <p className="a-quote-text">{text}</p>
          <div className="a-quote-attrib">{attrib}</div>
        </div>
      </div>
    </section>
  );
}

// ---- Final CTA ----
function FinalCTA(){
  const eyebrow = _C('about.cta.eyebrow', '· Solara Living · GTA ·');
  const headingRaw = _C('about.cta.heading', "Let's build\nsomething lasting.");
  const headingHtml = String(headingRaw).replace(/\n/g, '<br/>');
  const sub = _C('about.cta.subheading', "Tell us about your space. We'll walk you through the options and send a fixed quote within 48 hours.");
  const buttonText = _C('about.cta.buttonText', 'Request a quote');
  const buttonLink = _C('about.cta.buttonLink', 'contact.html');
  const phone = _C('site.phone', '416 708 8053');
  return (
    <section className="final-cta">
      <div className="wrap final-inner">
        <div className="final-eyebrow">{eyebrow}</div>
        <h2 className="final-headline display" dangerouslySetInnerHTML={{__html: headingHtml}} />
        <p className="final-sub">{sub}</p>
        <div className="final-actions">
          <a className="btn dark" href={buttonLink}>{buttonText} <span className="arrow">↗</span></a>
          <a className="btn ghost-dark" href={`tel:${String(phone).replace(/\D/g, '')}`}>{phone} <span className="arrow">→</span></a>
        </div>
      </div>
    </section>
  );
}

// ---- Footer ----
function Footer(){
  const tagline = _C('site.footerCopy', 'Premium outdoor living. Designed, delivered, and installed across the GTA and southern Ontario.');
  const phone = _C('site.phone', '416 708 8053');
  const email = _C('site.salesEmail', 'hello@solaraliving.ca');
  return (
    <footer className="footer">
      <div className="wrap footer-inner">
        <div className="footer-brand">
          <div className="wordmark">
            <img className="mark" src="images/solara-logo.svg" alt="" aria-hidden="true" />
            <span>SOLARA <b>LIVING</b></span>
          </div>
          <div className="small">{tagline}</div>
        </div>
        <div className="footer-cols">
          <div>
            <div className="col-h">Products</div>
            <a href="pergolas.html">Pergolas</a>
            <a href="saunas.html">Saunas</a>
            <a href="hot-tubs.html">Hot tubs</a>
            <a href="swim-spas.html">Swim spas</a>
          </div>
          <div>
            <div className="col-h">Company</div>
            <a href="about.html">About</a>
            <a href="contact.html">Contact</a>
            <a href="Solara Living.html#gallery">Projects</a>
          </div>
          <div>
            <div className="col-h">Get in touch</div>
            <a href={`tel:${String(phone).replace(/\D/g, '')}`}>{phone}</a>
            <a href={`mailto:${email}`}>{email}</a>
            <a href="contact.html">Book a consultation</a>
          </div>
        </div>
      </div>
      <div className="wrap footer-base">
        <span>© {new Date().getFullYear()} Solara Living Inc.</span>
        <span>7250 Keele St. Unit 255 · Vaughan ON L4K 1Z8</span>
      </div>
    </footer>
  );
}

// ---- App ----
function App(){
  const [navDark, setNavDark] = useState(true);
  useEffect(() => {
    const onScroll = () => setNavDark(window.scrollY < window.innerHeight * 0.7);
    onScroll();
    window.addEventListener("scroll", onScroll, {passive:true});
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <>
      <Nav onDark={navDark} />
      <Hero />
      <MarqueeStrip />
      <Story />
      <Partners />
      <Approach />
      <Scope />
      <Quote />
      <FinalCTA />
      <Footer />
    </>
  );
}

ReactDOM.createRoot(document.getElementById("root")).render(<App />);
